import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { getQuota } from "./api";

/**
 * Quota context — central store for "how many free reports does the visitor
 * have left?". Polled once on app boot and refreshed after every analysis
 * (Home triggers `refresh()` after a successful analyse so the banner can
 * reflect the new state without a full page reload).
 *
 * Why a context (not local state in Home / EvaluationSheet):
 * - The QuotaBanner sits at the top of the layout, the paywall modal sits at
 *   the root, and the print button sits deep in EvaluationSheet. Drilling
 *   props would be noisy; context keeps all three in sync.
 *
 * Shape of `quota`:
 *   { tier, reports_used, free_limit, remaining_free, credits, allowed,
 *     authenticated }
 *
 * `tier` is the policy-tier returned by /api/quota:
 *   "guest"   — anonymous, 1 free report max
 *   "free"    — logged in, 3 free reports max
 *   "credit"  — logged in, out of free but has paid credits
 *   "premium" — premium subscription active
 *   "admin"   — site owner (email in ADMIN_EMAILS) — unlimited, no counters
 */
const QuotaContext = createContext({
  quota: null,
  loading: true,
  refresh: async () => {},
  setQuotaFromAnalyze: () => {},
});

export const QuotaProvider = ({ children }) => {
  const [quota, setQuota] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const data = await getQuota();
      setQuota(data);
    } catch {
      setQuota(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Called by Home after a successful /analyze — the response carries the
  // post-consume quota envelope so we can update without a second request.
  const setQuotaFromAnalyze = useCallback((envelope) => {
    if (!envelope) return;
    setQuota((prev) => ({
      ...(prev || {}),
      tier: envelope.tier ?? prev?.tier ?? "guest",
      reports_used: envelope.reports_used ?? prev?.reports_used ?? 0,
      free_limit: envelope.free_limit ?? prev?.free_limit ?? 0,
      remaining_free:
        envelope.remaining_free ??
        (envelope.free_limit != null && envelope.reports_used != null
          ? Math.max(0, envelope.free_limit - envelope.reports_used)
          : prev?.remaining_free ?? 0),
      credits: envelope.credits ?? prev?.credits ?? 0,
      // After a consume the next call may not be allowed — recompute.
      allowed:
        envelope.tier === "premium" ||
        envelope.tier === "admin" ||
        (envelope.credits ?? 0) > 0 ||
        (envelope.remaining_free ?? 0) > 0,
      authenticated: prev?.authenticated ?? false,
    }));
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <QuotaContext.Provider value={{ quota, loading, refresh, setQuotaFromAnalyze }}>
      {children}
    </QuotaContext.Provider>
  );
};

export const useQuota = () => useContext(QuotaContext);
